import { memo } from 'react';
import { Button } from '@/components/ui/button';
import { Clock, Eye, Lock } from 'lucide-react';
import { CopyOnClick } from '@/components/planilha/CopyOnClick';
import { usePainelPedido } from '@/contexts/PainelPedidoContext';
import { PrazoSlaBadge } from '@/components/kanban/PrazoSlaBadge';
import {
  CancelarItemTriagemButton,
  ContatoPedidoCell,
  TimelineCnpjButton,
  formatCnpjTriagem,
  resumoProdutos,
} from '@/components/kanban/triagem/triagemPedidoUtils';
import {
  TRIAGEM_CELULA,
  TRIAGEM_CELULA_FORTE,
  TRIAGEM_CELULA_NUM,
  TRIAGEM_TD,
  TRIAGEM_TR_BASE,
  TRIAGEM_TR_LIVRE,
  TRIAGEM_TR_TOPO,
  TRIAGEM_TR_TRAVADO,
} from '@/components/kanban/triagem/triagemTabela';
import { cn } from '@/lib/utils';
import { DestinoRetomadaBadge } from './DestinoRetomadaBadge';
import type { AcaoFluxoTriagem, TriagemFluxoConfig } from './triagensFluxoConfig';
import type { PedidoTriagemFluxo } from './useTriagemFluxo';
import { TRIAGEM_BTN } from '@/components/kanban/triagem/triagemTabela';
import { AcoesTriagem, BotaoManter } from '@/components/kanban/triagem/AcoesTriagem';
import type { TtInputLinha } from '@/components/kanban/input/TtFixaInput';

interface TriagemFluxoRowProps {
  config: TriagemFluxoConfig;
  pedido: PedidoTriagemFluxo;
  posicao: number;
  isTopo: boolean;
  isProcessing: boolean;
  onAcao: (pedido: PedidoTriagemFluxo, acao: AcaoFluxoTriagem) => void;
  onManterFimFila: (pedido: PedidoTriagemFluxo) => void;
  onAfterCancel: () => void;
  tt?: TtInputLinha;
}

export const TriagemFluxoRow = memo(function TriagemFluxoRow({
  config,
  pedido,
  posicao,
  isTopo,
  isProcessing,
  onAcao,
  onManterFimFila,
  onAfterCancel,
  tt,
}: TriagemFluxoRowProps) {
  const { open: openPedido } = usePainelPedido();

  const travadoPelaFila = config.fifo && !isTopo;
  const acoesBloqueadas = travadoPelaFila || isProcessing;
  const bloqueadoTitle = 'Fila FIFO: trabalhe primeiro o pedido do topo';
  const { resumo, completo } = resumoProdutos(pedido.itens);
  const qtdTotal = pedido.itens.reduce((sum, i) => sum + (i.quantidade || 0), 0);

  /* Avançar = as ações de desfecho (hover verde, AVANCAR da config); o resto
     (refazer contrato, reinputar, pendência) é desvio. */
  const ehAvancar = (acao: AcaoFluxoTriagem) => acao.botaoClasse.includes('hover:!text-success');
  const acoesAvancar = config.acoes.filter(ehAvancar);
  const acoesDesvio = config.acoes.filter((acao) => !ehAvancar(acao));
  /* TT Fixa no Input: o ✓ espera a resposta da gerente e, com ela, não espera a
     fila (o e-mail manda o pedido para o fim). */
  const renderAcao = (acao: AcaoFluxoTriagem) => tt && acao.key === 'input-realizado' ? (
    tt.liberado ? (
      <Button key={acao.key} size="icon" variant="ghost" className={acao.botaoClasse}
        title="A gerente respondeu: confira a resposta e aperte para o Input realizado"
        onClick={() => onAcao(pedido, acao)} disabled={isProcessing}>
        <acao.Icone className="h-3.5 w-3.5" />
      </Button>
    ) : (
      <Button key={acao.key} size="icon" variant="ghost" className={acao.botaoClasse} disabled
        title="O input da TT Fixa só sai com a resposta da gerente de contas">
        <Lock className="h-3.5 w-3.5" />
      </Button>
    )
  ) : (
    <Button
      key={acao.key}
      size="icon"
      variant="ghost"
      className={acao.botaoClasse}
      title={travadoPelaFila ? bloqueadoTitle : acao.titulo}
      onClick={() => onAcao(pedido, acao)}
      disabled={acoesBloqueadas || (config.coluna === 'ATIVAÇÃO DE CHIP' && !pedido.ativacaoPronta)}
    >
      <acao.Icone className="h-3.5 w-3.5" />
    </Button>
  );

  /* Anatomia única das 23 triagens: linha de 38px, 1º da fila com filete bordô
     por dentro, travado esmaecido mas legível. */
  const rowClass = cn(
    TRIAGEM_TR_BASE,
    config.fifo && isTopo
      ? TRIAGEM_TR_TOPO
      : travadoPelaFila
        ? TRIAGEM_TR_TRAVADO
        : TRIAGEM_TR_LIVRE,
  );

  return (
    <tr className={rowClass}>
      <td className={TRIAGEM_TD}>
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold tabular-nums ${config.fifo && isTopo ? 'text-primary' : 'text-muted-foreground'}`}>
          {config.fifo && isTopo ? '▶' : null}
          {posicao + 1}º
          {pedido.manter_em && <Clock className="h-3 w-3 text-primary" aria-label="Passado para o fim da fila" />}
        </span>
      </td>

      <td className={TRIAGEM_TD}>
        <CopyOnClick value={pedido.documento || pedido.cnpj} className="text-[11px] tabular-nums text-muted-foreground">
          {formatCnpjTriagem(pedido.documento || pedido.cnpj)}
        </CopyOnClick>
      </td>

      <td className={TRIAGEM_TD}>
        {tt ? (
          <div className="flex min-w-0 flex-col justify-center">
            <span className={cn(TRIAGEM_CELULA_FORTE, 'leading-tight')} title={pedido.razao_social}>
              {pedido.razao_social || '-'}
            </span>
            {tt.situacao}
          </div>
        ) : (
          <span className={TRIAGEM_CELULA_FORTE} title={pedido.razao_social}>
            {pedido.razao_social || '-'}
          </span>
        )}
      </td>

      <td className={TRIAGEM_TD}>
        <span className={TRIAGEM_CELULA} title={`${pedido.consultor_nome}${pedido.equipe_nome ? ` • ${pedido.equipe_nome}` : ''}`}>
          {pedido.consultor_nome || '-'}{pedido.equipe_nome ? ` • ${pedido.equipe_nome}` : ''}
        </span>
      </td>

      <td className={TRIAGEM_TD}>
        <button
          onClick={(e) => { e.stopPropagation(); openPedido(pedido.pedido_id, 'visao360'); }}
          className="truncate text-[11px] font-bold text-primary tabular-nums hover:underline cursor-pointer"
          title="Abrir Painel do Pedido"
        >
          {pedido.numero_pedido}
        </button>
      </td>

      <td className={TRIAGEM_TD}>
        <span className={TRIAGEM_CELULA} title={completo}>
          {resumo || '-'}
        </span>
      </td>

      <td className={TRIAGEM_TD}>
        <span className={TRIAGEM_CELULA}>{qtdTotal || '-'}</span>
      </td>

      <td className={TRIAGEM_TD}>
        <span className={TRIAGEM_CELULA_NUM}>
          {pedido.valor_total ? `R$ ${pedido.valor_total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}
        </span>
      </td>

      <td className={TRIAGEM_TD}>
        {tt ? tt.prazo : <PrazoSlaBadge pedidoId={pedido.pedido_id} coluna={config.coluna} entradaColunaEm={pedido.entrada_coluna_em} prazoHoras={config.prazoHoras} />}
      </td>

      {config.mostrarCobrar && (
        <td className={TRIAGEM_TD}>
          <div className="flex items-center gap-1.5">
            <ContatoPedidoCell telefone={pedido.telefone} leadOrigemId={pedido.lead_origem_id} />
            <TimelineCnpjButton cnpj={pedido.cnpj} />
          </div>
        </td>
      )}

      {config.mostrarDestinoRetomada && (
        <td className={TRIAGEM_TD}>
          <DestinoRetomadaBadge itens={pedido.itens} />
        </td>
      )}

      <td className={TRIAGEM_TD}>
        <AcoesTriagem
          manter={config.permitirManterFimFila && (
            <BotaoManter
              title={travadoPelaFila ? bloqueadoTitle : config.rotuloManter
                ? `${config.rotuloManter}: ainda não deu para resolver? Justifique e passe para o fim da fila`
                : 'Não dá para tratar agora? Justifique e passe para o fim da fila'}
              onClick={() => onManterFimFila(pedido)}
              disabled={acoesBloqueadas}
            />
          )}
          avancar={acoesAvancar.map(renderAcao)}
          desvios={tt ? [<span key="tt-email" className="contents">{tt.email}</span>, ...acoesDesvio.map(renderAcao)] : acoesDesvio.map(renderAcao)}
          ferramentas={
            <Button
              size="icon"
              variant="ghost"
              className={TRIAGEM_BTN}
              title="Analisar — Abrir Painel do Pedido (Pedido 360)"
              onClick={() => openPedido(pedido.pedido_id, 'visao360')}
            >
              <Eye className="h-3 w-3" />
            </Button>
          }
          cancelar={
            <CancelarItemTriagemButton
              pedidoId={pedido.pedido_id}
              itens={pedido.itens}
              disabled={acoesBloqueadas}
              disabledTitle={bloqueadoTitle}
              onAfterCancel={onAfterCancel}
            />
          }
        />
      </td>
    </tr>
  );
});
